import type { ClassSessionDTO } from "@/modules/classes/domain/contracts/sessions";
import { classesApi } from "@/modules/classes/presentation/api/client";
import type { BookingDTO } from "@/modules/private-lessons/domain/contracts/bookings";
import { formatPrivateLessonBookingPlace } from "@/modules/private-lessons/domain/lib/booking-display";
import {
    isMultiMemberPackageKind,
    multiMemberPackageLabel,
} from "@/modules/private-lessons/domain/lib/package-kind";
import { privateLessonsApi } from "@/modules/private-lessons/presentation/api/client";
import type { RoomBookingCalendarDTO } from "@/modules/room-bookings/domain/contracts/room-bookings";
import { roomBookingsApi } from "@/modules/room-bookings/presentation/api/client";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

/** รายการสำหรับแสดงใน Check-in Hub (รวม ຊົ່ວໂມງຮຽນ + booking + room booking ของวันนั้น) */
export type CheckInListItem =
  | {
      type: "general_class";
      id: string;
      date: string;
      startTime: string;
      endTime: string;
      subjectOrStudent: string;
      subjectDetail?: string;
      roomName: string;
      instructorIds: string[];
      instructorNames: string[];
      instructors: ClassSessionDTO["instructors"];
      capacity: number;
      attendanceCount: number;
      /** ຈຳນວນຄູທີ່ check-in ແລ້ວ (ສຳລັບ Hub / Partner progress) */
      instructorCheckInCount: number;
      engagementKind: ClassSessionDTO["engagementKind"];
      locationKind: ClassSessionDTO["locationKind"];
      status: "scheduled" | "completed" | "cancelled";
    }
  | {
      type: "private_lesson";
      id: string;
      startTime: string;
      endTime: string;
      subjectOrStudent: string;
      subjectDetail?: string;
      roomName: string;
      instructorIds: string[];
      instructorNames: string[];
      status: "scheduled" | "completed" | "cancelled";
      studentId?: string;
      studentMemberId?: string;
      packageKind?: "individual" | "group";
      groupSize?: number | null;
      memberAttendanceCount?: number;
    }
  | {
      type: "room_booking";
      id: string;
      startTime: string;
      endTime: string;
      subjectOrStudent: string;
      subjectDetail?: string;
      roomName: string;
      instructorNames: string[];
      status: "scheduled" | "completed" | "cancelled";
    };

export type CheckInListResponse = {
  items: CheckInListItem[];
  totalSessions: number;
  checkedInCount: number;
};

function toCheckInItemSession(s: ClassSessionDTO): CheckInListItem {
  return {
    type: "general_class",
    id: s.id,
    date: s.date,
    startTime: s.startTime,
    endTime: s.endTime,
    subjectOrStudent: s.templateName,
    subjectDetail:
      s.classTypeName && s.levelName
        ? `Course ID: ${s.classTypeId}-${s.levelId}`
        : undefined,
    roomName: s.roomName || s.offSiteLabel || "",
    instructorIds: Array.isArray(s.instructorIds) ? s.instructorIds : [],
    instructorNames: Array.isArray(s.instructorNames) ? s.instructorNames : [],
    instructors: Array.isArray(s.instructors) ? s.instructors : [],
    capacity: s.capacity,
    attendanceCount: s.attendanceCount ?? 0,
    instructorCheckInCount: s.instructorCheckInCount ?? 0,
    engagementKind: s.engagementKind,
    locationKind: s.locationKind,
    status: s.status,
  };
}

function toCheckInItemBooking(b: BookingDTO): CheckInListItem {
  const isGroup = isMultiMemberPackageKind(b.packageKind);
  return {
    type: "private_lesson",
    id: b.id,
    startTime: b.startTime,
    endTime: b.endTime,
    subjectOrStudent: isGroup
      ? `${multiMemberPackageLabel(b.packageKind) ?? "ກຸ່ມ"}${b.groupSize != null ? ` (${b.groupSize} ຄົນ)` : ""}: ${b.studentName}`
      : b.studentName,
    subjectDetail: b.packageTemplateName,
    roomName: formatPrivateLessonBookingPlace({
      locationKind: b.locationKind,
      offSiteDistance: b.offSiteDistance,
      offSiteLabel: b.offSiteLabel,
      roomName: b.roomName,
    }),
    instructorIds: Array.isArray(b.instructorIds) ? b.instructorIds : [],
    instructorNames: Array.isArray(b.instructorNames) ? b.instructorNames : [],
    status: b.status,
    studentId: b.studentId,
    studentMemberId: b.studentMemberId,
    packageKind: b.packageKind,
    groupSize: b.groupSize ?? null,
    memberAttendanceCount: b.memberAttendanceCount ?? 0,
  };
}

function toCheckInItemRoomBooking(r: RoomBookingCalendarDTO): CheckInListItem {
  return {
    type: "room_booking",
    id: r.id,
    startTime: r.startTime,
    endTime: r.endTime,
    subjectOrStudent: r.title ?? r.contactName ?? "ຈອງຫ້ອງ",
    subjectDetail: r.contactName ?? undefined,
    roomName: r.roomName ?? "",
    instructorNames: [],
    status: r.status,
  };
}

/**
 * ดึงรายการ ຊົ່ວໂມງຮຽນ + booking + room booking ของวันนั้น สำหรับ Check-in Hub
 * ใช้ classes/sessions/calendar, private-lessons/bookings, room-bookings/calendar
 */
export async function getCheckInList(
  date: string,
): Promise<CheckInListResponse> {
  const [sessionsRes, bookingsRes, roomBookingsRes] = await Promise.all([
    classesApi.sessions.getCalendar(date, date),
    privateLessonsApi.bookings.list({
      limit: 500,
      offset: 0,
      filters: [{ field: "date", op: "eq", value: date }],
    }),
    roomBookingsApi.getCalendar(date, date),
  ]);

  const sessions = sessionsRes.data ?? [];
  const bookings = bookingsRes.data ?? [];
  const roomBookings = roomBookingsRes.data ?? [];

  const sessionItems = sessions.map(toCheckInItemSession);
  const bookingItems = bookings.map(toCheckInItemBooking);
  const roomBookingItems = roomBookings.map(toCheckInItemRoomBooking);
  const all: CheckInListItem[] = [
    ...sessionItems,
    ...bookingItems,
    ...roomBookingItems,
  ].sort((a, b) => a.startTime.localeCompare(b.startTime));

  const classSessionIds = sessionItems.map((s) => s.id);
  const eligibleCountsRes =
    classSessionIds.length > 0
      ? await classesApi.sessions.getEligibleCounts(classSessionIds)
      : { counts: {} as Record<string, number> };
  const eligibleCounts = eligibleCountsRes.counts ?? {};

  // totalSessions = ຄລາດສ໌ມາດຕະຖານ: ຕາມ eligible; Partner: ຕາມຈຳນວນຄູ assign; ອື່ນ: 1 ຕໍ່ລາຍການ
  const totalSessions = all.reduce((sum, item) => {
    if (item.status === "cancelled") return sum;
    if (item.type === "general_class") {
      if (item.engagementKind === "partner_instructor_only") {
        const n = item.instructorIds.length;
        return sum + (n > 0 ? n : 1);
      }
      return sum + (eligibleCounts[item.id] ?? item.capacity ?? 0);
    }
    if (item.type === "private_lesson" || item.type === "room_booking")
      return sum + 1;
    return sum;
  }, 0);
  const checkedInCount = all.reduce((sum, item) => {
    if (item.type === "general_class") {
      if (item.engagementKind === "partner_instructor_only") {
        return sum + item.instructorCheckInCount;
      }
      return sum + item.attendanceCount;
    }
    if (item.type === "private_lesson" && item.status === "completed")
      return sum + 1;
    if (item.type === "room_booking" && item.status === "completed")
      return sum + 1;
    return sum;
  }, 0);

  return {
    items: all,
    totalSessions,
    checkedInCount,
  };
}

/** Check-in class ຊົ່ວໂມງຮຽນ */
export async function checkInClassSession(
  sessionId: string,
  body: { studentId: string; overrideReason?: string },
): Promise<{ attendanceId: string; consumptionId: string }> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/check-in`,
    body,
  );
}

/** Bulk check-in class ຊົ່ວໂມງຮຽນ */
export type BulkCheckInClassSessionResponse = {
  succeeded: Array<{
    studentId: string;
    attendanceId: string;
    consumptionId: string;
  }>;
  failed: Array<{
    studentId: string;
    error: string;
  }>;
  instructorSucceeded: Array<{
    instructorId: string;
    instructorCheckInId: string;
  }>;
  instructorFailed: Array<{
    instructorId: string;
    error: string;
  }>;
};

export async function bulkCheckInClassSession(
  sessionId: string,
  body: { studentIds: string[]; instructorIds?: string[] },
): Promise<BulkCheckInClassSessionResponse> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/bulk-check-in`,
    body,
  );
}

/** Check-in private lesson booking (มีอยู่แล้วใน private-lessons API) */
export type CheckInPrivateBookingResponse = {
  attendanceId: string;
  consumptionId: string;
  instructorSucceeded: Array<{
    instructorId: string;
    instructorCheckInId: string;
  }>;
  instructorFailed: Array<{
    instructorId: string;
    error: string;
  }>;
};

export async function checkInPrivateBooking(
  bookingId: string,
  body?: {
    overrideReason?: string;
    instructorIds?: string[];
    memberStudentIds?: string[];
  },
): Promise<CheckInPrivateBookingResponse> {
  return fetcher.post<CheckInPrivateBookingResponse>(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/check-in`,
    body ?? {},
  );
}

/** Check-in instructor to class ຊົ່ວໂມງຮຽນ */
export async function checkInInstructorClassSession(
  sessionId: string,
  body: { instructorId: string },
): Promise<{ instructorCheckInId: string }> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/instructor-check-in`,
    body,
  );
}

/** Undo check-in student from class ຊົ່ວໂມງຮຽນ */
export async function undoCheckInClassSession(
  sessionId: string,
  body: { attendanceId: string },
): Promise<{
  attendanceId: string;
  consumptionId: string;
  studentId: string;
  packageId: string;
  sessionsRestored: number;
}> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/undo-check-in`,
    body,
  );
}

/** Undo check-in instructor from class ຊົ່ວໂມງຮຽນ */
export async function undoCheckInInstructorClassSession(
  sessionId: string,
  body: { instructorCheckInId: string },
): Promise<{
  instructorCheckInId: string;
  instructorId: string;
}> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/undo-instructor-check-in`,
    body,
  );
}

/** Undo check-in student from private lesson booking */
export async function undoCheckInPrivateBooking(
  bookingId: string,
  body: { attendanceId: string },
): Promise<{
  attendanceId: string;
  consumptionId: string;
  studentId: string;
  packageId: string;
  bookingId: string;
  hoursRestored: number;
}> {
  return fetcher.post(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/undo-check-in`,
    body,
  );
}

/** Undo check-in instructor from private lesson booking */
export async function undoCheckInInstructorPrivateBooking(
  bookingId: string,
  body: { instructorCheckInId: string },
): Promise<{
  instructorCheckInId: string;
  instructorId: string;
}> {
  return fetcher.post(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/undo-instructor-check-in`,
    body,
  );
}

/** Bulk undo check-in from class ຊົ່ວໂມງຮຽນ */
export type BulkUndoCheckInClassSessionResponse = {
  studentSucceeded: Array<{
    attendanceId: string;
    consumptionId: string;
    studentId: string;
    packageId: string;
    sessionsRestored: number;
  }>;
  studentFailed: Array<{
    attendanceId: string;
    error: string;
  }>;
  instructorSucceeded: Array<{
    instructorCheckInId: string;
    instructorId: string;
  }>;
  instructorFailed: Array<{
    instructorCheckInId: string;
    error: string;
  }>;
};

export async function bulkUndoCheckInClassSession(
  sessionId: string,
  body: { attendanceIds: string[]; instructorCheckInIds?: string[] },
): Promise<BulkUndoCheckInClassSessionResponse> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/bulk-undo-check-in`,
    body,
  );
}

/** Bulk undo check-in from private lesson booking */
export type BulkUndoCheckInBookingResponse = {
  studentSucceeded?: {
    attendanceId: string;
    consumptionId: string;
    studentId: string;
    packageId: string;
    bookingId: string;
    hoursRestored: number;
  };
  studentFailed?: {
    attendanceId: string;
    error: string;
  };
  instructorSucceeded: Array<{
    instructorCheckInId: string;
    instructorId: string;
  }>;
  instructorFailed: Array<{
    instructorCheckInId: string;
    error: string;
  }>;
};

export async function bulkUndoCheckInPrivateBooking(
  bookingId: string,
  body: { attendanceId?: string; instructorCheckInIds?: string[] },
): Promise<BulkUndoCheckInBookingResponse> {
  return fetcher.post(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/bulk-undo-check-in`,
    body,
  );
}

/** Check-in instructor to private lesson booking */
export async function checkInInstructorPrivateBooking(
  bookingId: string,
  body: { instructorId: string },
): Promise<{ instructorCheckInId: string }> {
  return fetcher.post(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/instructor-check-in`,
    body,
  );
}

/** Substitute instructor check-in for class ຊົ່ວໂມງຮຽນ */
export async function substituteInstructorCheckInClassSession(
  sessionId: string,
  body: {
    instructorId: string;
    substituteForInstructorId?: string | null;
    substituteReason?: string | null;
  },
): Promise<{
  instructorCheckInId: string;
  instructorId: string;
  instructorName: string;
  isSubstitute: true;
}> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/substitute-instructor-check-in`,
    body,
  );
}

/** Guest instructor check-in for class ຊົ່ວໂມງຮຽນ */
export async function guestInstructorCheckInClassSession(
  sessionId: string,
  body: {
    name: string;
    phoneNumber?: string | null;
    email?: string | null;
    substituteForInstructorId?: string | null;
    substituteReason?: string | null;
    ratePerSession?: number | null;
    payoutBasis?: "per_hour" | "per_session";
  },
): Promise<{
  instructorId: string;
  instructorCheckInId: string;
  instructorName: string;
  isSubstitute: true;
  isGuest: true;
}> {
  return fetcher.post(
    `${config.apiUrl}/classes/sessions/${sessionId}/guest-instructor-check-in`,
    body,
  );
}

/** Substitute instructor check-in for private lesson booking */
export async function substituteInstructorCheckInPrivateBooking(
  bookingId: string,
  body: {
    instructorId: string;
    substituteForInstructorId?: string | null;
    substituteReason?: string | null;
  },
): Promise<{
  instructorCheckInId: string;
  instructorId: string;
  instructorName: string;
  isSubstitute: true;
}> {
  return fetcher.post(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/substitute-instructor-check-in`,
    body,
  );
}

/** Guest instructor check-in for private lesson booking */
export async function guestInstructorCheckInPrivateBooking(
  bookingId: string,
  body: {
    name: string;
    phoneNumber?: string | null;
    email?: string | null;
    substituteForInstructorId?: string | null;
    substituteReason?: string | null;
    ratePerSession?: number | null;
    payoutBasis?: "per_hour" | "per_session";
  },
): Promise<{
  instructorId: string;
  instructorCheckInId: string;
  instructorName: string;
  isSubstitute: true;
  isGuest: true;
}> {
  return fetcher.post(
    `${config.apiUrl}/private-lessons/bookings/${bookingId}/guest-instructor-check-in`,
    body,
  );
}

/** รายการ attendance records (นักเรียน) */
export async function getAttendanceRecords(query: {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: Array<{ field: string; op: string; value: unknown }>;
}): Promise<{
  data: Array<{
    id: string;
    studentId: string;
    studentName: string;
    studentMemberId: string;
    checkedInAt: string;
    sessionType: string;
    className?: string;
    classSessionId?: string;
    privateLessonBookingId?: string;
    privateLessonDate?: string;
    privateLessonStartTime?: string;
    privateLessonEndTime?: string;
    classTypeName?: string;
    levelName?: string;
    roomName?: string;
    instructorName?: string;
    packageName?: string;
    privateLessonPackageName?: string;
    consumed: string;
    hasOverride: boolean;
    overrideReason?: string;
    checkedInByName?: string;
  }>;
  meta: { total: number; offset: number; limit: number };
}> {
  const url = new URL(`${config.apiUrl}/attendance/records`);
  url.searchParams.set("limit", String(query.limit ?? 20));
  url.searchParams.set("offset", String(query.offset ?? 0));
  if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
  if (query.filters)
    url.searchParams.set("filters", JSON.stringify(query.filters));
  return fetcher.get(url.toString());
}

/** รายการ instructor check-ins */
export async function getInstructorCheckInRecords(query: {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: Array<{ field: string; op: string; value: unknown }>;
}): Promise<{
  data: Array<{
    id: string;
    instructorId: string;
    instructorName: string;
    checkedInAt: string;
    sessionType: string;
    className?: string;
    classSessionId?: string;
    privateLessonBookingId?: string;
    privateLessonDate?: string;
    privateLessonStartTime?: string;
    privateLessonEndTime?: string;
    roomName?: string;
    studentName?: string;
    checkedInByName?: string;
  }>;
  meta: { total: number; offset: number; limit: number };
}> {
  const url = new URL(`${config.apiUrl}/attendance/instructor-records`);
  url.searchParams.set("limit", String(query.limit ?? 20));
  url.searchParams.set("offset", String(query.offset ?? 0));
  if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
  if (query.filters)
    url.searchParams.set("filters", JSON.stringify(query.filters));
  return fetcher.get(url.toString());
}

/** อัปเดต room booking เป็น completed (สำหรับ Check-in Hub) */
export async function completeRoomBooking(
  bookingId: string,
): Promise<{ success: boolean }> {
  return fetcher.put<{ success: boolean }>(
    `${config.apiUrl}/room-bookings/${bookingId}/complete`,
    {},
  );
}

export async function patchInstructorCheckInTime(
  checkInId: string,
  body: { checkedInAt: string },
): Promise<{
  id: string;
  checkedInAt: string;
  sessionType: "general_class" | "private_lesson" | "room_booking";
}> {
  return fetcher.patch(
    `${config.apiUrl}/attendance/instructor-check-ins/${checkInId}/check-in-time`,
    body,
  );
}

export async function patchAttendanceRecordTime(
  recordId: string,
  body: { checkedInAt: string },
): Promise<{
  id: string;
  checkedInAt: string;
  sessionType: "general_class" | "private_lesson";
}> {
  return fetcher.patch(
    `${config.apiUrl}/attendance/records/${recordId}/check-in-time`,
    body,
  );
}
